import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getAllMembers, getRegisteredMembers, updateMemberStatus } from "../api/memberApi";

const MembersListPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [updateStatus, setUpdateStatus] = useState("");
  const [updateNoofshares, setUpdateNoofshares] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [stateFilter, setStateFilter] = useState("All");
  const [memberTypeFilter, setMemberTypeFilter] = useState("All");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const token = localStorage.getItem("adminToken");
  const isAdmin = localStorage.getItem("role") === "admin" && !!token;
  const BASE_URL = "https://api.jshmcsl.com";

  const fetchMembers = async () => {
    try {
      const role = localStorage.getItem("role"); // admin or user?

      let data;

      if (role === "admin") {
        data = await getAllMembers();
        setMembers(data.members || data.data || []);
      } else {
        data = await getRegisteredMembers();
        setMembers(data || []);
      }

    } catch (err) {
      setError("Failed to load members.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchMembers();
  }, []);

  const handleAdminUpdate = async (memberId, status, no_of_shares) => {
    if (!status) return alert("Select a status");
    if (!no_of_shares) return alert("Enter membership ID");

    try {
      await updateMemberStatus(memberId, status, no_of_shares);
      alert("Updated successfully!");

      setSelectedMember(null);
      fetchMembers();
    } catch (err) {
      alert("Failed to update member");
    }
  };

  // Mask Phone
  const maskPhone = (phone) => {
    if (!phone) return "";
    return phone.slice(0, 2) + "*****" + phone.slice(-2);
  };

  // Mask Email
  const maskEmail = (email) => {
    if (!email) return "";
    const [user, domain] = email.split("@");

    const maskedUser =
      user.length <= 2
        ? user[0] + "*"
        : user.slice(0, 2) + "*".repeat(user.length - 2);

    return maskedUser + "@" + domain;
  };

  const getFullImageUrl = (path) => {
    if (!path) return "";
    return `${BASE_URL}${path}`;
  };

  const filteredMembers = members.filter((m) => {
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      m.name.toLowerCase().includes(search) ||
      (m.phone && m.phone.includes(search)) ||
      (m.email && m.email.toLowerCase().includes(search)) ||
      (m.membershipId && m.membershipId.toLowerCase().includes(search));

    const matchesStatus =
      statusFilter === "All" ? true : m.status === statusFilter;

    const matchesState =
      stateFilter === "All"
        ? true
        : m.state && m.state.toUpperCase().trim() === stateFilter.toUpperCase();

    const matchesMemberType =
      memberTypeFilter === "All"
        ? true
        : m.memberType &&
        m.memberType.toLowerCase().trim() === memberTypeFilter.toLowerCase().trim();

    return matchesSearch && matchesStatus && matchesState && matchesMemberType;
  });

  // PAGINATION
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMembers = filteredMembers.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const downloadExcel = () => {
    const formatted = members.map((m, i) => ({
      "S.No": i + 1,
      "Name": m.name,
      "Membership ID": m.membershipId || "N/A",
      "Member Type": m.memberType,
      "Shares Amount": (Number(m.no_of_shares) * 100) + 100,
      "State": m.state,
      "Phone": m.phone,
      "Email": m.email,
      "Status": m.status,
      "Joined Date": new Date(m.createdAt).toLocaleDateString("en-IN")
    }));

    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Members");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    saveAs(blob, "Members_List.xlsx");
  };

  return (
    <section className="relative py-20 bg-gradient-to-br from-white via-[#fffaf4] to-[#fff1dc] overflow-hidden">
      <div className="max-w-7xl mx-auto md:px-8 mt-24 md:mt-40">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-primary mb-3">
            Our <span className="text-accent">Members</span>
          </h2>
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 px-3">

          {/* Search Bar */}
          <input
            type="text"
            placeholder="Search name, phone, member ID..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="border px-4 py-2 rounded-xl shadow-sm w-full md:w-1/3"
          />

          <div>

            {isAdmin && (
              <div className="mb-4 flex justify-end">
                <button
                  onClick={downloadExcel}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700"
                >
                  Download Excel
                </button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              {/* State Filter */}
              <select
                value={stateFilter}
                onChange={(e) => {
                  setStateFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="border px-4 py-2 rounded-xl shadow-sm w-full sm:w-40"
              >
                <option value="All">All States</option>
                <option value="TAMIL NADU">Tamil Nadu</option>
                <option value="PUDUCHERRY">Puducherry</option>
              </select>

              {/* Member Type Filter */}
              <select
                value={memberTypeFilter}
                onChange={(e) => {
                  setMemberTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="border px-4 py-2 rounded-xl shadow-sm w-full sm:w-48"
              >
                <option value="All">All Member Types</option>
                <option value="Ordinary">Ordinary</option>
                <option value="Nominal/Associate">Nominal/Associate</option>
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="border px-4 py-2 rounded-xl shadow-sm w-full sm:w-40"
              >
                <option value="All">All Status</option>
                <option value="Submitted">Submitted</option>
                <option value="Verified">Verified</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {loading && (
          <p className="text-center text-gray-500 font-medium">
            Loading members...
          </p>
        )}
        {error && (
          <p className="text-center text-red-500 font-medium">{error}</p>
        )}

        {!loading && !error && (
          <div className="overflow-x-auto bg-white backdrop-blur-md rounded-2xl shadow-lg border border-[#ffe3bd]">
            <table className="w-full text-left text-gray-700 text-sm md:text-base">
              <thead>
                <tr className="bg-gradient-to-r from-amber-100 to-orange-100 text-primary font-semibold text-xs md:text-sm uppercase">
                  <th className="px-2 py-3 md:px-5 md:py-4">#</th>
                  <th className="px-2 py-3 md:px-5 md:py-4">Name</th>
                  <th className="px-2 py-3 md:px-5 md:py-4">Member-ID</th>
                  <th className="px-2 py-3 md:px-5 md:py-4">Member Type</th>
                  <th className="px-2 py-3 md:px-5 md:py-4">Share Amount</th>
                  <th className="px-2 py-3 md:px-5 md:py-4">State</th>
                  <th className="px-2 py-3 md:px-5 md:py-4 hidden md:table-cell">Address</th>
                  <th className="px-2 py-3 md:px-5 md:py-4">Status</th>
                  <th className="px-2 py-3 md:px-5 md:py-4">Joined</th>
                  {isAdmin && <th className="px-2 py-3 md:px-5 md:py-4">Action</th>}
                </tr>
              </thead>

              <tbody>
                {paginatedMembers.map((m, i) => (
                  <tr
                    key={m._id}
                    className="border-b border-[#ffe5c4] hover:bg-amber-50 transition-all duration-300 align-middle"
                  >
                    <td className="px-2 py-3 md:px-5 md:py-4 font-bold text-primary">
                      {(currentPage - 1) * itemsPerPage + (i + 1)}
                    </td>
                    {/* NAME FULL */}
                    <td className="px-2 py-3 md:px-5 md:py-4 whitespace-nowrap">{m.name}</td>

                    <td className="px-2 py-3 md:px-5 md:py-4">{m.membershipId || "N/A"}</td>
                    <td className="px-2 py-3 md:px-5 md:py-4 whitespace-nowrap">{m.memberType}</td>
                    <td className="px-2 py-3 md:px-5 md:py-4 font-semibold whitespace-nowrap text-accent"> {(Number(m.no_of_shares) * 100) + 100}</td>
                    <td className="px-2 py-3 md:px-5 md:py-4 whitespace-nowrap">{m.state}</td>


                    <td className="px-2 py-3 md:px-5 md:py-4 hidden md:table-cell max-w-xs overflow-hidden text-ellipsis">
                      {m.address}
                    </td>

                    <td className="px-2 py-3 md:px-5 md:py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-[10px] md:text-xs font-bold
                ${m.status === "Approved" && "bg-green-100 text-green-700"}
                ${m.status === "Verified" && "bg-blue-100 text-blue-700"}
                ${m.status === "Submitted" && "bg-yellow-100 text-yellow-700"}
                ${m.status === "Rejected" && "bg-red-100 text-red-700"}
              `}
                      >
                        {m.status}
                      </span>
                    </td>

                    <td className="px-2 py-3 md:px-5 md:py-4">
                      {new Date(m.createdAt).toLocaleDateString("en-IN")}
                    </td>

                    {isAdmin && (
                      <td className="px-2 py-3 md:px-5 md:py-4">
                        <button
                          onClick={() => {
                            setSelectedMember(m);
                            setUpdateStatus(m.status || "");
                            setUpdateNoofshares(m.no_of_shares || "");
                          }}
                          className="px-3 py-1 md:px-4 md:py-1.5 rounded-lg shadow text-white 
                bg-gradient-to-r from-amber-400 to-orange-500  
                hover:from-amber-500 hover:to-orange-600 
                transition-all text-xs md:text-sm"
                        >
                          View
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>


          </div>
        )}
        {/* PAGINATION */}
        <div className="flex items-center justify-center gap-2 mt-6">

          {/* PREV BUTTON */}
          <button
            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
            className="px-4 py-2 rounded-lg bg-orange-200 hover:bg-orange-300"
          >
            Prev
          </button>

          {/* PAGE NUMBERS */}
          {(() => {
            let pages = [];

            // Always show first page
            if (currentPage > 2) {
              pages.push(1);
            }

            // Ellipsis before current block
            if (currentPage > 3) {
              pages.push("...");
            }

            // Current - 1
            if (currentPage > 1) {
              pages.push(currentPage - 1);
            }

            // Current page
            pages.push(currentPage);

            // Current + 1
            if (currentPage < totalPages) {
              pages.push(currentPage + 1);
            }

            // Ellipsis after current block
            if (currentPage < totalPages - 2) {
              pages.push("...");
            }

            // Last page
            if (currentPage < totalPages - 1) {
              pages.push(totalPages);
            }

            return pages.map((page, idx) => (
              <button
                key={idx}
                onClick={() => page !== "..." && setCurrentPage(page)}
                disabled={page === "..."}
                className={`px-3 py-1 rounded-lg ${currentPage === page
                  ? "bg-orange-500 text-white"
                  : "bg-orange-100"
                  }`}
              >
                {page}
              </button>
            ));
          })()}

          {/* NEXT BUTTON */}
          <button
            onClick={() =>
              currentPage < totalPages && setCurrentPage(currentPage + 1)
            }
            className="px-4 py-2 rounded-lg bg-orange-200 hover:bg-orange-300"
          >
            Next
          </button>

        </div>


      </div>

      {/* Modal for Admin */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-3xl p-8 relative 
      max-h-[90vh] overflow-y-auto"
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedMember(null)}
              className="absolute top-4 right-4 text-gray-600 hover:text-black text-2xl font-bold"
            >
              ✕
            </button>

            {/* Title */}
            <h3 className="text-3xl font-extrabold text-primary mb-6 border-b pb-3">
              {selectedMember.name}'s{" "}
              <span className="text-accent"> Application</span>
            </h3>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-[15px] leading-relaxed">
              <p>
                <span className="font-semibold text-gray-700">Gender:</span>{" "}
                {selectedMember.gender}
              </p>
              <p>
                <span className="font-semibold text-gray-700">State:</span>{" "}
                {selectedMember.state}
              </p>

              <p>
                <span className="font-semibold text-gray-700">Phone:</span>{" "}
                {selectedMember.phone}
              </p>
              <p>
                <span className="font-semibold text-gray-700">Email:</span>{" "}
                {selectedMember.email}
              </p>

              <p className="md:col-span-2">
                <span className="font-semibold text-gray-700">Address:</span>{" "}
                {selectedMember.address}
              </p>

              <p>
                <span className="font-semibold text-gray-700">Aadhaar No:</span>{" "}
                {selectedMember.aadhaarNo}
              </p>
              <p>
                <span className="font-semibold text-gray-700">PAN No:</span>{" "}
                {selectedMember.panNumber}
              </p>

              <p>
                <span className="font-semibold text-gray-700">Member Type:</span>{" "}
                {selectedMember.memberType}
              </p>
              <p>
                <span className="font-semibold text-gray-700">No.of Shares:</span>{" "}
                {(Number(selectedMember.no_of_shares) * 100) + 100}
              </p>

              <p>
                <span className="font-semibold text-gray-700">Caste:</span>{" "}
                {selectedMember.caste}
              </p>

              {/* Status Badge */}
              <div className="col-span-2 mt-2">
                <span className="font-semibold text-gray-700">Status:</span>{" "}
                <span
                  className={`px-4 py-1.5 rounded-full text-sm font-bold
              ${selectedMember.status === "Approved"
                      ? "bg-green-100 text-green-700"
                      : selectedMember.status === "Verified"
                        ? "bg-blue-100 text-blue-700"
                        : selectedMember.status === "Submitted"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                    }
            `}
                >
                  {selectedMember.status}
                </span>
              </div>
            </div>

            {/* Documents Section */}
            <div className="mt-8 p-5 border rounded-2xl bg-gradient-to-br from-orange-50 to-white shadow-inner">
              <h4 className="font-bold text-lg text-gray-800 mb-4">
                Documents
              </h4>

              <div className="flex flex-col gap-4">
                {selectedMember.aadhaarDoc && (
                  <a
                    href={getFullImageUrl(selectedMember.aadhaarDoc)}
                    target="_blank"
                    className="flex items-center gap-3 px-4 py-3 bg-white border shadow-sm rounded-xl hover:shadow-md hover:bg-orange-100 transition"
                  >
                    <span className="text-xl">📄</span>
                    <span className="font-medium text-orange-700">
                      View Aadhaar Document
                    </span>
                  </a>
                )}

                {selectedMember.paymentScreenshot && (
                  <a
                    href={getFullImageUrl(selectedMember.paymentScreenshot)}
                    target="_blank"
                    className="flex items-center gap-3 px-4 py-3 bg-white border shadow-sm rounded-xl hover:shadow-md hover:bg-green-100 transition"
                  >
                    <span className="text-xl">🧾</span>
                    <span className="font-medium text-green-700">
                      View Payment Screenshot
                    </span>
                  </a>
                )}
              </div>
            </div>

            {/* Admin Controls */}
            <div className="mt-8 p-5 bg-gray-50 border rounded-2xl">
              <h4 className="font-semibold text-gray-800 mb-4">
                Admin Controls
              </h4>

              {/* STATUS + MEMBERSHIP ID -> FLEX 2 COLUMNS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Status */}
                <div>
                  <label className="font-semibold block mb-1">
                    Update Status:
                  </label>
                  <select
                    value={updateStatus}
                    onChange={(e) => setUpdateStatus(e.target.value)}
                    className="border px-3 py-2 rounded-lg shadow-sm w-full"
                  >
                    <option value="Submitted">Submitted</option>
                    <option value="Verified">Verified</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>

                {/* Membership ID */}
                <div>
                  <label className="font-semibold block mb-1">
                    No of Shares:
                  </label>
                  <input
                    type="text"
                    placeholder="1"
                    value={updateNoofshares}
                    onChange={(e) =>
                      setUpdateNoofshares(e.target.value)
                    }
                    className="border px-3 py-2 rounded-lg w-full"
                  />
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={() =>
                  handleAdminUpdate(
                    selectedMember._id,
                    updateStatus,
                    updateNoofshares
                  )
                }
                className="mt-5 w-full py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl shadow hover:opacity-90"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MembersListPage;
